/* eslint-disable indent */
/* eslint-disable no-console */
import { useEffect, useState, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { FiSave } from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList } from "recharts";
import { useAuthContext } from "../../context/auth";
import {
  getClientesPublicitarios,
  getIntermediarios,
  updateCliente,
  getOportunidades,
  getCategorias,
  getUsers,
} from "../../utils";
import "./styles.scss";

export const ListaClientesMisVentas = () => {
  const [clientes, setClientesPublicitarios] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClienteId, setEditingClienteId] = useState(null);
  const [ultimo_contacto_fecha, setUltimoContactoFecha] = useState(null);
  const [ultimo_contacto_accion, setUltimoContactoAccion] = useState("");
  const [todasLasOportunidades, setTodasLasOportunidades] = useState([]);
  const [ventasPorCliente, setVentasPorCliente] = useState({});
  const [oportunidadesRelevantes, setOportunidadesRelevantes] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroMes, setFiltroMes] = useState([]);
  const [filtroEstatus, setFiltroEstatus] = useState([]);
  const [filtroIntermediario, setFiltroIntermediario] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [sumasFiltradas, setSumasFiltradas] = useState({
    "100%": 0,
    "90%": 0,
    "75%": 0,
    "50%": 0,
    "0%": 0,
  });
  const [proximo_contacto_fecha, setProximoContactoFecha] = useState(null);
  const [proximo_contacto_accion, setProximoContactoAccion] = useState("");
  const [usuarios, setUsuarios] = useState([]);

  const { pais, rol, id } = useAuthContext() || {};

  const getPaisCode = (countryName) => {
    switch (countryName.toLowerCase()) {
      case "Uruguay":
        return "URU";
      case "Chile":
        return "CHI";
      case "Perú":
        return "PER";
      default:
        return countryName;
    }
  };

  const toUpperCase = (text) => text?.toString().toUpperCase();

  const localString = useCallback(
    (pais) =>
      ({
        Chile: "es-CL",
        Perú: "es-PE",
        Uruguay: "es-UY",
      })[pais] || "es-UY",
    []
  );

  const moneda = useCallback(
    (pais) =>
      ({
        Chile: "$",
        Perú: "S/",
        Uruguay: "$",
      })[pais] || "$",
    []
  );

  const decimales = useCallback(
    (pais) =>
      ({
        Chile: 0,
        Perú: 2,
        Uruguay: 0,
      })[pais] || 0,
    []
  );

  const mesEnLetras = useCallback((mes) => {
    const mesesEnLetras = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
    return mesesEnLetras[parseInt(mes) - 1] || "";
  }, []);

  const formatDateWithoutYear = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = mesEnLetras(String(date.getUTCMonth() + 1).padStart(2, "0"));
      return `${day} ${month}`;
    }
    return "-";
  };

  const handleMesChange = useCallback((e) => {
    const selectedMonths = Array.from(
      e.target.closest(".dropdown-menu").querySelectorAll("input[type=checkbox]:checked"),
      (checkbox) => checkbox.value
    );
    setFiltroMes(selectedMonths);
  }, []);

  const renderMesOptions = () =>
    ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"].map((mes, index) => (
      <div key={index} className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`mes${index}`}
          value={index < 9 ? `0${index + 1}` : `${index + 1}`}
          checked={filtroMes.includes(index < 9 ? `0${index + 1}` : `${index + 1}`)}
          onChange={handleMesChange}
          style={{ marginLeft: "-15px" }}
        />
        <label className="form-check-label" htmlFor={`mes${index}`} style={{ marginLeft: "10px" }}>
          {mes}
        </label>
      </div>
    ));

  const renderClientesOptions = () => {
    const clientesOrdenados = [...new Set(todasLasOportunidades.map((oportunidad) => oportunidad.cliente))].sort();
    return clientesOrdenados.map((cliente, index) => (
      <option key={index} value={cliente}>
        {cliente}
      </option>
    ));
  };

  const renderIntermediariosOptions = () => {
    const intermediariosOrdenados = [
      ...new Set(todasLasOportunidades.map((oportunidad) => oportunidad.intermediario)),
    ].sort();
    return intermediariosOrdenados.map((intermediario, index) => (
      <option key={index} value={intermediario}>
        {intermediario}
      </option>
    ));
  };

  const handleEstatusChange = (e) => {
    const value = e.target.value;
    setFiltroEstatus((prev) => (prev.includes(value) ? prev.filter((estatus) => estatus !== value) : [...prev, value]));
  };

  const renderEstatusOptions = () =>
    ["100%", "90%", "75%", "50%", "0%"].map((estatus, index) => (
      <div key={index} className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`estatus${index}`}
          value={estatus}
          checked={filtroEstatus.includes(estatus)}
          onChange={handleEstatusChange}
          style={{ marginLeft: "-15px" }}
        />
        <label className="form-check-label" htmlFor={`estatus${index}`} style={{ marginLeft: "10px" }}>
          {estatus}
        </label>
      </div>
    ));

  const renderCategoriaOptions = () =>
    categorias.map((categoria, index) => (
      <option key={index} value={categoria.nombre}>
        {categoria.nombre}
      </option>
    ));

  const handleCategoriaChange = (e) => {
    setFiltroCategoria(e.target.value);
  };

  const handleUpdateCliente = async (
    clienteId,
    newEjecutivo,
    newAgencia,
    ultimoFecha,
    ultimoAccion,
    proximoFecha,
    proximoAccion,
    newContactoId
  ) => {
    try {
      const cliente = clientes.find((c) => c.id === clienteId);

      const updatedData = {
        id: clienteId,
        ejecutivo: newEjecutivo !== undefined ? newEjecutivo : cliente.ejecutivo,
        agencia: newAgencia !== undefined ? newAgencia : cliente.agencia,
        ultimo_contacto_fecha: ultimoFecha !== null ? ultimoFecha : cliente.ultimo_contacto_fecha,
        ultimo_contacto_accion: ultimoAccion !== "" ? ultimoAccion : cliente.ultimo_contacto_accion,
        proximo_contacto_fecha: proximoFecha !== null ? proximoFecha : cliente.proximo_contacto_fecha,
        proximo_contacto_accion: proximoAccion !== "" ? proximoAccion : cliente.proximo_contacto_accion,
        contactoId: newContactoId !== null ? newContactoId : cliente.contactoId,
      };

      await updateCliente(updatedData);

      setClientesPublicitarios(
        clientes.map((cliente) => (cliente.id === clienteId ? { ...cliente, ...updatedData } : cliente))
      );
      setEditingClienteId(null);
      setUltimoContactoFecha(null);
      setUltimoContactoAccion("");
      setProximoContactoFecha(null);
      setProximoContactoAccion("");
    } catch (error) {
      console.error("Error updating cliente:", error.message);
    }
  };

  const calculateVentasYOportunidades = (oportunidades, clientes) => {
    const ventasCliente = {};
    const oportunidadesCliente = {};
    oportunidades.forEach((oportunidad) => {
      if (oportunidad.ano === 2025 && oportunidad.ejecutivo === id.toString()) {
        const clienteMatch = clientes.find((c) => c.nombre === oportunidad.cliente);
        if (clienteMatch) {
          const clienteId = clienteMatch.id;
          if (oportunidad.estatus === "100%") {
            ventasCliente[clienteId] = (ventasCliente[clienteId] || 0) + Number(oportunidad.importe);
          }
          if (["75%", "90%"].includes(oportunidad.estatus)) {
            oportunidadesCliente[clienteId] = (oportunidadesCliente[clienteId] || 0) + Number(oportunidad.importe);
          }
        }
      }
    });
    return [ventasCliente, oportunidadesCliente];
  };

  const filtrarOportunidades = () =>
    todasLasOportunidades.filter(
      (oportunidad) =>
        (!filtroCliente || oportunidad.cliente.toLowerCase().includes(filtroCliente.toLowerCase())) &&
        (filtroMes.length === 0 || filtroMes.includes(oportunidad.mes.padStart(2, "0"))) &&
        (filtroEstatus.length === 0 || filtroEstatus.includes(oportunidad.estatus)) &&
        (filtroIntermediario === "" || oportunidad.intermediario === filtroIntermediario) &&
        (filtroCategoria === "" || oportunidad.categoria === filtroCategoria)
    );

  const calcularSumasFiltradas = useCallback(() => {
    const sumas = {
      "100%": 0,
      "90%": 0,
      "75%": 0,
      "50%": 0,
      "0%": 0,
    };

    todasLasOportunidades.forEach((oportunidad) => {
      if (
        oportunidad.ano === 2025 &&
        oportunidad.ejecutivo === id.toString() && // Here we filter by the executive's ID
        (!filtroCliente || oportunidad.cliente.toLowerCase().includes(filtroCliente.toLowerCase())) &&
        (filtroMes.length === 0 || filtroMes.includes(oportunidad.mes.padStart(2, "0"))) &&
        (filtroEstatus.length === 0 || filtroEstatus.includes(oportunidad.estatus)) &&
        (filtroIntermediario === "" || oportunidad.intermediario === filtroIntermediario) &&
        (filtroCategoria === "" || oportunidad.categoria === filtroCategoria)
      ) {
        sumas[oportunidad.estatus] += Number(oportunidad.importe);
      }
    });

    setSumasFiltradas(sumas);
  }, [todasLasOportunidades, filtroCliente, filtroMes, filtroEstatus, filtroIntermediario, filtroCategoria, id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const paisCode = getPaisCode(pais);
        const [clientesData, intermediariosData, oportunidadesData, categoriasData] = await Promise.all([
          getClientesPublicitarios({ pais: paisCode }),
          getIntermediarios({ pais: paisCode }),
          getOportunidades({ pais: paisCode }),
          getCategorias(),
          getUsers(),
        ]);

        const oportunidadesNoEliminadas = oportunidadesData.filter((oportunidad) => !oportunidad.eliminada);
        setTodasLasOportunidades(oportunidadesNoEliminadas);
        setCategorias(categoriasData);

        let filteredClientes = clientesData;
        if (rol === "comercial") {
          filteredClientes = clientesData.filter((cliente) => cliente.ejecutivo === id.toString());
        }

        const [ventasCliente, oportunidadesCliente] = calculateVentasYOportunidades(
          oportunidadesNoEliminadas,
          filteredClientes
        );

        const fetchedUsers = await getUsers();
        if (fetchedUsers) {
          setUsuarios(fetchedUsers);
        }

        setVentasPorCliente(ventasCliente);
        setOportunidadesRelevantes(oportunidadesCliente);
        setClientesPublicitarios(filteredClientes);
        setIntermediarios(intermediariosData);
        setLoading(false);
      } catch (error) {
        console.error("Error al inicializar datos:", error);
        toast.error("Hubo un error al cargar los datos. Inténtalo de nuevo.");
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais, id, rol, localString, moneda, decimales]);

  useEffect(() => {
    calcularSumasFiltradas();
  }, [calcularSumasFiltradas]);

  useEffect(() => {
    const filteredOportunidades = filtrarOportunidades();
    const [newVentasCliente, newOportunidadesCliente] = calculateVentasYOportunidades(filteredOportunidades, clientes);
    setVentasPorCliente(newVentasCliente);
    setOportunidadesRelevantes(newOportunidadesCliente);
  }, [filtroCliente, filtroMes, filtroEstatus, filtroIntermediario, filtroCategoria, todasLasOportunidades, clientes]);

  const [ordenamiento, setOrdenamiento] = useState({
    criterio: "100%",
    direccion: "desc",
    porFecha: null,
  });

  const filtrarClientes = useCallback(() => {
    let sortedClientes = clientes.filter((cliente) => {
      const clienteMatch = !filtroCliente || cliente.nombre.toLowerCase().includes(filtroCliente.toLowerCase());
      const categoriaMatch = !filtroCategoria || cliente.categoria === filtroCategoria;
      const agenciaMatch = !filtroIntermediario || cliente.agencia === filtroIntermediario;
      const mesMatch =
        filtroMes.length === 0 ||
        todasLasOportunidades.some(
          (op) => op.cliente === cliente.nombre && filtroMes.includes(op.mes.padStart(2, "0")) && op.ano === 2025
        );
      const estatusMatch =
        filtroEstatus.length === 0 ||
        todasLasOportunidades.some(
          (op) => op.cliente === cliente.nombre && filtroEstatus.includes(op.estatus) && op.ano === 2025
        );

      return clienteMatch && categoriaMatch && agenciaMatch && mesMatch && estatusMatch;
    });

    const parseDate = (dateString, isFuture = false) => {
      if (!dateString) return isFuture ? new Date(8640000000000000) : new Date(-8640000000000000);
      return new Date(dateString);
    };

    sortedClientes = !ordenamiento.porFecha
      ? sortedClientes.sort((a, b) => {
          const a100 = ventasPorCliente[a.id] || 0;
          const b100 = ventasPorCliente[b.id] || 0;
          if (a100 !== b100) {
            return ordenamiento.direccion === "desc" ? b100 - a100 : a100 - b100;
          }
          const a90_75 = oportunidadesRelevantes[a.id] || 0;
          const b90_75 = oportunidadesRelevantes[b.id] || 0;
          return ordenamiento.direccion === "desc" ? b90_75 - a90_75 : a90_75 - b90_75;
        })
      : sortedClientes.sort((a, b) => {
          const isLastContact = ordenamiento.porFecha && ordenamiento.porFecha.includes("last");
          const isOldest = ordenamiento.porFecha && ordenamiento.porFecha.includes("Oldest");
          const dateField = isLastContact ? "ultimo_contacto_fecha" : "proximo_contacto_fecha";

          const aDate = a[dateField] ? parseDate(a[dateField], !isLastContact) : null;
          const bDate = b[dateField] ? parseDate(b[dateField], !isLastContact) : null;

          // Manejo de fechas nulas o indefinidas
          if (aDate === null && bDate === null) return 0;
          if (aDate === null) return 1;
          if (bDate === null) return -1;

          // Comparación de fechas válidas
          return isLastContact ? (isOldest ? aDate - bDate : bDate - aDate) : isOldest ? bDate - aDate : aDate - bDate;
        });

    return sortedClientes;
  }, [
    clientes,
    filtroCliente,
    filtroCategoria,
    filtroIntermediario,
    filtroMes,
    filtroEstatus,
    todasLasOportunidades,
    ventasPorCliente,
    oportunidadesRelevantes,
    ordenamiento,
  ]);

  const getRadius = (key, data) => {
    const keys = Object.keys(data).filter((key) => data[key] > 0);
    const firstKey = keys[0];
    const lastKey = keys.at(-1);
    if (firstKey === key && lastKey === key) {
      return [10, 10, 10, 10];
    } else if (firstKey === key) {
      return [10, 0, 0, 10];
    } else if (lastKey === key) {
      return [0, 10, 10, 0];
    }
    return [0, 0, 0, 0];
  };

  const getBootstrapClassForStatus = (status) => {
    switch (status) {
      case "100%":
        return "bg-success";
      case "90%":
        return "bg-danger";
      case "75%":
        return "bg-primary";
      case "50%":
        return "bg-warning";
      case "0%":
        return "bg-black";
      default:
        return "bg-secondary";
    }
  };

  const getColorForStatus = (status) => {
    switch (status) {
      case "100%":
        return "#198754"; // Verde
      case "90%":
        return "#dc3545"; // Rojo
      case "75%":
        return "#0d6efd"; // Azul
      case "50%":
        return "#ffc107"; // Amarillo
      case "0%":
        return "#000000"; // Negro
      default:
        return "#555555"; // Gris por defecto
    }
  };

  const getLabelColorForStatus = (status) => (status === "50%" ? "#000" : "#fff");

  const data = [
    {
      name: "Total",
      "100%": (sumasFiltradas["100%"] / (Object.values(sumasFiltradas).reduce((a, b) => a + b, 0) || 1)) * 100 || 0,
      "90%": (sumasFiltradas["90%"] / (Object.values(sumasFiltradas).reduce((a, b) => a + b, 0) || 1)) * 100 || 0,
      "75%": (sumasFiltradas["75%"] / (Object.values(sumasFiltradas).reduce((a, b) => a + b, 0) || 1)) * 100 || 0,
      "50%": (sumasFiltradas["50%"] / (Object.values(sumasFiltradas).reduce((a, b) => a + b, 0) || 1)) * 100 || 0,
      "0%": (sumasFiltradas["0%"] / (Object.values(sumasFiltradas).reduce((a, b) => a + b, 0) || 1)) * 100 || 0,
      empty: Object.values(sumasFiltradas).reduce((a, b) => a + b, 0) === 0 ? 100 : 0,
    },
  ];

  return (
    <div className="container">
      {loading ? (
        <div>Cargando...</div>
      ) : (
        <>
          <div className="row justify-content-center mt-2 mb-2">
            <div className="col-md-2">
              <select
                className={`form-control ${filtroCliente ? "active-filter" : ""}`}
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
              >
                <option value="">Cliente</option>
                {renderClientesOptions()}
              </select>
            </div>
            <div className="col-md-2">
              <Dropdown>
                <Dropdown.Toggle
                  variant="white border"
                  id="dropdown-basic"
                  className={`form-control text-start ${filtroMes.length > 0 ? "active-filter" : ""}`}
                >
                  {filtroMes.length > 0 ? filtroMes.map((mes) => mesEnLetras(mes)).join(", ") : "Mes/meses"}
                </Dropdown.Toggle>
                <Dropdown.Menu>{renderMesOptions()}</Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="col-md-2">
              <Dropdown>
                <Dropdown.Toggle
                  variant="white border"
                  id="dropdown-status"
                  className={`form-control text-start ${filtroEstatus.length > 0 ? "active-filter" : ""}`}
                >
                  {filtroEstatus.length > 0 ? filtroEstatus.join(", ") : "Status"}
                </Dropdown.Toggle>
                <Dropdown.Menu>{renderEstatusOptions()}</Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="col-md-2">
              <select
                className={`form-control ${filtroCategoria ? "active-filter" : ""}`}
                value={filtroCategoria}
                onChange={handleCategoriaChange}
              >
                <option value="">Categoría</option>
                {renderCategoriaOptions()}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className={`form-control ${filtroIntermediario ? "active-filter" : ""}`}
                value={filtroIntermediario}
                onChange={(e) => setFiltroIntermediario(e.target.value)}
              >
                <option value="">Intermediario</option>
                {renderIntermediariosOptions()}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className={`form-control ${ordenamiento.porFecha ? "active-filter" : ""}`}
                value={ordenamiento.porFecha || ""}
                onChange={(e) => setOrdenamiento({ ...ordenamiento, porFecha: e.target.value || null })}
              >
                <option value="">Contacto</option>
                <option value="lastRecent">Último + reciente</option>
                <option value="lastOldest">Último - reciente</option>
                <option value="nextRecent">Próximo + reciente</option>
                <option value="nextOldest">Próximo - reciente</option>
              </select>
            </div>
          </div>
          <div className="mb-2 d-flex justify-content-center">
            <div className="d-flex justify-content-between" style={{ width: "80%" }}>
              {["100%", "90%", "75%", "50%", "0%"].map((estatus, index) => (
                <div
                  key={index}
                  className={`text-center p-2 ${getBootstrapClassForStatus(estatus)} border border-dark m-3 text-${estatus === "50%" ? "dark" : "white"}`}
                  style={{ borderRadius: "4px" }}
                >
                  <div>
                    <strong>{estatus}</strong>
                  </div>
                  <div>
                    {sumasFiltradas[estatus] > 0
                      ? `${moneda(pais)} ${sumasFiltradas[estatus].toLocaleString(localString(pais), { maximumFractionDigits: decimales(pais), minimumFractionDigits: decimales(pais) })}`
                      : "-"}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="d-flex justify-content-center">
            <ResponsiveContainer width="80%" height={80} className="p-0 m-0">
              <BarChart layout="vertical" data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <XAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
                <YAxis type="category" dataKey="name" hide />
                {data[0].empty > 0 ? (
                  <Bar dataKey="empty" fill="#555555" isAnimationActive={false} radius={[10, 10, 10, 10]}>
                    <LabelList dataKey="empty" position="center" formatter={() => "SIN DATOS"} fill="#FFF" />
                  </Bar>
                ) : (
                  ["100%", "90%", "75%", "50%", "0%"].map(
                    (estatus) =>
                      data[0][estatus] > 0 && (
                        <Bar
                          key={estatus}
                          dataKey={estatus}
                          stackId="a"
                          fill={getColorForStatus(estatus)}
                          radius={getRadius(estatus, data[0])}
                        >
                          <LabelList
                            dataKey={estatus}
                            position="center"
                            formatter={(value) => `${Math.round(value)}%`}
                            fill={getLabelColorForStatus(estatus)}
                          />
                        </Bar>
                      )
                  )
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="table table-bordered table-hover table-striped">
            <thead className="table-dark">
              <tr>
                <th width="15%" className="text-center">
                  {toUpperCase("CLIENTE")}
                </th>
                <th width="20%" className="text-center">
                  {toUpperCase("CONTACTO")}
                </th>
                <th width="15%" className="text-center bg-success text-white">
                  {toUpperCase("100%")}
                </th>
                <th width="15%" className="text-center" style={{ backgroundColor: "#613E8D" }}>
                  {" "}
                  {toUpperCase("90% + 75%")}
                </th>
                <th colSpan="2" width="15%" className="text-center">
                  {toUpperCase("ÚLTIMO CONTACTO")}
                </th>
                <th colSpan="2" width="15%" className="text-center">
                  {toUpperCase("PRÓXIMO CONTACTO")}
                </th>
                <th width="3%" className="text-center">
                  {toUpperCase("EDIT")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtrarClientes().map((cliente, index) => (
                <tr key={index}>
                  <td className="text-start">{toUpperCase(cliente.nombre)}</td>
                  <td className="text-start">
                    {editingClienteId === cliente.id && (rol === "superadmin" || rol === "comercial") ? (
                      <select
                        className="small-input"
                        style={{ width: "200px" }}
                        value={cliente.contactoId || ""}
                        onChange={(e) => {
                          const newContactoId = e.target.value;
                          setClientesPublicitarios(
                            clientes.map((c) =>
                              c.id === cliente.id ? { ...c, contactoId: Number(newContactoId) || null } : c
                            )
                          );
                        }}
                      >
                        <option className="text-start" value="">
                          Seleccionar
                        </option>
                        {usuarios
                          .filter((user) => ["agencia", "cliente"].includes(user.rol))
                          .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`))
                          .map((user) => {
                            // Encuentra el intermediario correspondiente
                            const intermediario = intermediarios.find((inter) => inter.id === user.agencia);
                            return (
                              <option key={user.id} value={user.id} className="text-start">
                                {`${user.nombre} ${user.apellido}${intermediario ? `, ${intermediario.nombre}` : ""}`}
                              </option>
                            );
                          })}
                      </select>
                    ) : cliente.contactoId && usuarios.find((user) => user.id === cliente.contactoId) ? (
                      // Aquí también necesitas mostrar el intermediario
                      (() => {
                        const contacto = usuarios.find((user) => user.id === cliente.contactoId);
                        const intermediario = intermediarios.find((inter) => inter.id === contacto.agencia);
                        return `${contacto.nombre} ${contacto.apellido}${intermediario ? `, ${intermediario.nombre}` : ""}`;
                      })()
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-end">
                    {ventasPorCliente[cliente.id] !== undefined && ventasPorCliente[cliente.id] !== 0
                      ? `${moneda(pais)} ${ventasPorCliente[cliente.id].toLocaleString(localString(pais), { maximumFractionDigits: decimales(pais), minimumFractionDigits: decimales(pais) })}`
                      : "-"}
                  </td>
                  <td className="text-end">
                    {oportunidadesRelevantes[cliente.id] !== undefined && oportunidadesRelevantes[cliente.id] !== 0
                      ? `${moneda(pais)} ${oportunidadesRelevantes[cliente.id].toLocaleString(localString(pais), { maximumFractionDigits: decimales(pais), minimumFractionDigits: decimales(pais) })}`
                      : "-"}
                  </td>
                  {/* Último Contacto */}
                  <td className="text-center">
                    {editingClienteId === cliente.id && (rol === "superadmin" || rol === "comercial") ? (
                      <DatePicker
                        selected={ultimo_contacto_fecha}
                        onChange={(date) => setUltimoContactoFecha(date)}
                        dateFormat="dd MMM"
                        className="small-input"
                      />
                    ) : cliente.ultimo_contacto_fecha ? (
                      formatDateWithoutYear(cliente.ultimo_contacto_fecha)
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-center">
                    {editingClienteId === cliente.id && (rol === "superadmin" || rol === "comercial") ? (
                      <select
                        value={ultimo_contacto_accion}
                        className="small-input"
                        onChange={(e) => setUltimoContactoAccion(e.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        <option value="RRSS">{toUpperCase("RRSS")}</option>
                        <option value="CALL">{toUpperCase("CALL")}</option>
                        <option value="MAIL">{toUpperCase("MAIL")}</option>
                        <option value="MEET">{toUpperCase("MEET")}</option>
                        <option value="QUOTE">{toUpperCase("QUOTE")}</option>
                        <option value="SALE!">{toUpperCase("SALE!")}</option>
                      </select>
                    ) : (
                      toUpperCase(cliente.ultimo_contacto_accion || "-")
                    )}
                  </td>
                  {/* Próximo Contacto */}
                  <td className="text-center">
                    {editingClienteId === cliente.id && (rol === "superadmin" || rol === "comercial") ? (
                      <DatePicker
                        selected={proximo_contacto_fecha}
                        onChange={(date) => setProximoContactoFecha(date)}
                        dateFormat="dd MMM"
                        className="small-input"
                      />
                    ) : cliente.proximo_contacto_fecha ? (
                      formatDateWithoutYear(cliente.proximo_contacto_fecha)
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-center">
                    {editingClienteId === cliente.id && (rol === "superadmin" || rol === "comercial") ? (
                      <select
                        value={proximo_contacto_accion}
                        className="small-input"
                        onChange={(e) => setProximoContactoAccion(e.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        <option value="RRSS">{toUpperCase("RRSS")}</option>
                        <option value="CALL">{toUpperCase("CALL")}</option>
                        <option value="MAIL">{toUpperCase("MAIL")}</option>
                        <option value="MEET">{toUpperCase("MEET")}</option>
                        <option value="QUOTE">{toUpperCase("QUOTE")}</option>
                        <option value="SALE!">{toUpperCase("SALE!")}</option>
                      </select>
                    ) : (
                      toUpperCase(cliente.proximo_contacto_accion || "-")
                    )}
                  </td>
                  {/* Fin de Próximo Contacto */}
                  <td className="text-center">
                    {rol === "superadmin" || rol === "comercial" ? (
                      editingClienteId === cliente.id ? (
                        <FiSave
                          onClick={() =>
                            handleUpdateCliente(
                              cliente.id,
                              cliente.ejecutivo,
                              cliente.agencia,
                              ultimo_contacto_fecha,
                              ultimo_contacto_accion,
                              proximo_contacto_fecha,
                              proximo_contacto_accion,
                              cliente.contactoId
                            )
                          }
                        />
                      ) : (
                        <FaEdit onClick={() => setEditingClienteId(cliente.id)} />
                      )
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};
